import { Outlet } from 'react-router'
import { BreadcrumbObjectType } from '~/common/generated/apollo-operations'
import { chartRoute } from '~/components/charts/ChartRoutes'
import { dossbotRoute } from '~/components/dossbot/DossbotRoutes'
import { pivotTableRoute } from '~/components/pivot-table/PivotTableRoutes'
import { embedRoute } from '~/components/renderers/views/EmbedRoutes'
import { settingsRoute } from '~/components/settings/SettingsRoutes'
import { tableRoute } from '~/components/tables/TableRoutes'
import { workflowRoute } from '~/components/workflows/WorkflowRoutes'
import { RouteObject } from '~/router/types'
import { ErrorBoundary } from './ErrorBoundary'
import { AdminGuardLayout, AppLayout, PublicLayout, RootLayout } from './layouts'
import { lazy } from './lazy'

const OT = BreadcrumbObjectType

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    HydrateFallback: () => null,
    children: [
      // ── Public routes (no auth required) ──────────────────────────
      {
        element: <PublicLayout />,
        handle: { isAuth: true, showLeftNav: false },
        children: [
          { path: 'auth/login', lazy: lazy(() => import('../views/auth/login')) },
          { path: 'auth/sign-up', lazy: lazy(() => import('../views/auth/sign-up')) },
          {
            path: 'auth/forgot-password',
            lazy: lazy(() => import('../views/auth/forgot-password')),
          },
          { path: 'auth/reset-password', lazy: lazy(() => import('../views/auth/reset-password')) },
          { path: 'auth/set-password', lazy: lazy(() => import('../views/auth/set-password')) },
          { path: 'auth/confirmation', lazy: lazy(() => import('../views/auth/confirmation')) },
          { path: 'oauth/authorize', lazy: lazy(() => import('../views/oauth/authorize')) },
          { path: 'oauth/callback', lazy: lazy(() => import('../views/oauth/callback')) },
        ],
      },

      // ── App routes (require auth, except shared paths) ────────────
      {
        element: <AppLayout />,
        errorElement: <ErrorBoundary title="Page Error" />,
        handle: { tracksPageLoad: true },
        children: [
          // Shared routes — no auth required but render inside full layout
          {
            path: 'shared',
            Component: Outlet,
            handle: { isShared: true, showLeftNav: false },
            children: [
              {
                path: 'document/:document_id',
                lazy: lazy(
                  () => import('../components/ui/molecules/document-renderer/DocumentRenderer'),
                ),
                handle: { objectType: OT.DOCUMENT },
              },
              {
                path: 'form/:form_id',
                lazy: lazy(() => import('../views/shared/form/[form_id]')),
                handle: { objectType: OT.FORM },
              },
              {
                path: 'table/:table_id',
                lazy: lazy(() => import('../components/tables/SharedTablePage')),
                handle: { objectType: OT.TABLE },
              },
            ],
          },

          // Home
          {
            index: true,
            lazy: lazy(() => import('../components/home/Home')),
            handle: {
              pageType: 'home',
              group: 'home',
              showActivityFeed: true,
              objectType: OT.PROJECT,
            },
          },

          // ── Domain routes (colocated route files) ──────────────
          tableRoute,
          chartRoute,
          embedRoute,
          pivotTableRoute,
          workflowRoute,
          dossbotRoute,
          settingsRoute,

          // ── Standalone routes ───────────────────────────────────
          {
            path: 'form/:form_id',
            lazy: lazy(() => import('../components/forms/FormController')),
            handle: {
              pageType: 'form',
              group: 'forms',
              showLeftNav: false,
              showBackLink: true,
              objectType: OT.FORM,
            },
          },
          {
            path: 'record/:record_id',
            lazy: lazy(() => import('../components/records/RecordControllerComponent')),
            handle: {
              pageType: 'record',
              showActivityFeed: true,
              showBackLink: true,
              objectType: OT.TABLE_ROW,
            },
          },
          {
            path: 'document/:document_id',
            lazy: lazy(
              () => import('../components/ui/molecules/document-renderer/DocumentRenderer'),
            ),
            handle: { showActivityFeed: true, objectType: OT.DOCUMENT },
          },
          {
            // :project_id is a root workspace id
            path: 'project/:project_id',
            lazy: lazy(() => import('../components/home/Home')),
            handle: { group: 'home', showActivityFeed: true, objectType: OT.PROJECT },
          },
          {
            path: 'workspace/:workspace_id',
            lazy: lazy(() => import('../components/workspaces/WorkspaceController')),
            handle: { pageType: 'workspace', objectType: OT.WORKSPACE },
          },
          {
            path: 'forms',
            lazy: lazy(() => import('../components/forms-home/FormsController')),
            handle: { pageType: 'formsHome', group: 'forms', crumb: 'title_bar.forms' },
          },

          // ── Admin-only routes ──────────────────────────────────
          {
            element: <AdminGuardLayout />,
            children: [
              {
                path: 'table/new',
                lazy: lazy(
                  () => import('../components/table-settings/TableSettingsControllerComponent'),
                ),
                handle: { pageType: 'createTable', showLeftNav: false, objectType: OT.TABLE },
              },
              {
                path: 'chart/new',
                lazy: lazy(() => import('../components/charts/NewChartController')),
                handle: { pageType: 'createChart', showLeftNav: false, objectType: OT.CHART },
              },
              {
                path: 'pivot/new',
                lazy: lazy(() => import('../components/pivot-table/PivotTableSettingsController')),
                handle: {
                  pageType: 'createPivotTable',
                  showLeftNav: false,
                  objectType: OT.PIVOT_TABLE,
                },
              },
              {
                path: 'form/new',
                lazy: lazy(() => import('../views/form/new/index')),
                handle: {
                  pageType: 'createForm',
                  showLeftNav: false,
                  objectType: OT.FORM,
                  group: 'forms',
                  crumb: 'title_bar.new_form',
                },
              },
              {
                path: 'form/:form_id/edit',
                lazy: lazy(() => import('../components/form-settings/FormSettingsController')),
                handle: {
                  pageType: 'formSettings',
                  showLeftNav: false,
                  showBackLink: true,
                  objectType: OT.FORM,
                  group: 'forms',
                },
              },
              {
                path: 'workspace/new',
                lazy: lazy(() => import('../components/workspaces/NewWorkspaceController')),
                handle: { pageType: 'createWorkspace', objectType: OT.WORKSPACE },
              },
              {
                path: 'dependency-explorer',
                lazy: lazy(() => import('../components/dependency-explorer/DependencyExplorer')),
                handle: {
                  pageType: 'dependencyExplorer',
                  group: 'dependencyExplorer',
                  showLeftNav: false,
                  crumb: 'title_bar.dependency_explorer',
                },
              },
            ],
          },

          // ── Utility / dev ──────────────────────────────────────
          {
            path: 'apollo',
            lazy: lazy(() => import('../views/apollo/index')),
            handle: {
              pageType: 'apollo',
              showLeftNav: false,
              crumb: 'title_bar.graphql_explorer',
            },
          },
          {
            path: 'storybook',
            lazy: lazy(() => import('../views/storybook/index')),
            handle: {
              pageType: 'storybook',
              showLeftNav: false,
              crumb: 'title_bar.storybook',
            },
          },
          { path: 'u', lazy: lazy(() => import('../views/u/index')) },
          { path: 'ui/chips', lazy: lazy(() => import('../views/ui/chips')) },
          { path: 'ui/input', lazy: lazy(() => import('../views/ui/input')) },
        ],
      },

      // Catch-all 404
      { path: '*', lazy: lazy(() => import('../@/components/not-found')) },
    ],
  },
]
